import { Dialog } from '@cimplico/components';
import { useState } from 'react';
import { toastNotification } from '../../components/toast/Toast';
import { useUpdateWorksheets } from '../../queries/use-update-worksheets';
import { User, UserOverview } from '../../types/user';
import { ReconciliationWorksheet, GenericWorksheet, Worksheet } from '../../types/worksheet';
import UserSelect from '../../components/user-select/UserSelect';
import { intercomEvents } from '../../intercom-events';
import { useIntercom } from 'react-use-intercom';

type RemoveAssigneesDialogProps = {
    onClose: () => void;
    worksheets: Worksheet[];
};

function flattenAssigned(worksheets: Worksheet[]): string[] {
    const assigned: UserOverview[] = [];
    worksheets.forEach((ws) => {
        ws.assigned.forEach((wa) => {
            if (!assigned.find((a) => a.id === wa.id)) {
                assigned.push(wa);
            }
        });
    });

    return Array.from(new Set(assigned.map((a) => a.id)));
}

function RemoveAssigneesDialog({ onClose, worksheets }: RemoveAssigneesDialogProps): JSX.Element {
    const updateWorksheetsQuery = useUpdateWorksheets();
    const [assignees, setAssignees] = useState<User[]>([]);
    const { trackEvent } = useIntercom();

    function removeAssignees(): void {
        updateWorksheetsQuery.mutate(
            worksheets.map((ws) => {
                return {
                    id: ws.id,
                    fields: { assigned: ws.assigned.filter((a) => !assignees.find((assignee) => assignee.id === a.id)).map((a) => a.id) },
                };
            }),
            {
                onSuccess: () => {
                    onClose();
                    trackEvent(intercomEvents.worksheet.unassigned.event, intercomEvents.worksheet.unassigned.metaData(worksheets.length));
                    toastNotification('success', `${assignees.length} ${assignees.length === 1 ? 'user' : 'users'} unassigned!`);
                },

                onError: (err) => {
                    onClose();
                    toastNotification(
                        'error',
                        `Unable to unassign worksheet users!`,
                        err.response?.data.results[0].error.fields.errorMessage
                    );
                },
            }
        );
    }

    return (
        <Dialog
            title="Remove Assignees"
            description="Select the users you wish to remove from the selected worksheets."
            type="error"
            isOpen
            primaryAction="Remove Assignees"
            secondaryAction="Cancel"
            onClose={onClose}
            onPrimaryActionClick={removeAssignees}
            onSecondaryActionClick={onClose}
            isLoading={updateWorksheetsQuery.isLoading}
        >
            <div className="pr-6 pb-4">
                <UserSelect onChange={setAssignees} isMulti toIncludeIds={flattenAssigned(worksheets)} />
            </div>
        </Dialog>
    );
}
export default RemoveAssigneesDialog;
